import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactElement,
  type ReactNode,
} from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { srsService } from '../../services/srsService';
import {
  STEAMROOM_STREAM_URL,
  ZERODAY_STREAM_URL,
} from './srsConstants';
import {
  useSquawkStream,
  type SquawkStatus,
  type UseSquawkStreamResult,
} from './useSquawkStream';

interface MobileAudioContextValue {
  steamroom: UseSquawkStreamResult | null;
  zeroday: UseSquawkStreamResult | null;
  canSteamroom: boolean;
  canZeroday: boolean;
  anyLive: boolean;
  anyIntended: boolean;
  stopAll: () => void;
}

const MobileAudioContext = createContext<MobileAudioContextValue | null>(null);

function useMediaSession(
  streams: Array<{ label: string; status: SquawkStatus; stop: () => void; start: () => Promise<void> }>,
): void {
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    const live = streams.filter((s) => s.status === 'live' || s.status === 'connecting');
    const titles = live.map((s) => s.label);

    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: titles.length > 0 ? titles.join(' + ') : 'FZN Audio',
        artist: 'FZN Trading',
        album: 'Squawk',
      });
      navigator.mediaSession.playbackState =
        live.length > 0 ? 'playing' : 'paused';

      navigator.mediaSession.setActionHandler('play', () => {
        for (const s of streams) {
          if (s.status === 'disconnected' || s.status === 'error' || s.status === 'muted') {
            void s.start();
          }
        }
      });
      navigator.mediaSession.setActionHandler('pause', () => {
        for (const s of streams) s.stop();
      });
      navigator.mediaSession.setActionHandler('stop', () => {
        for (const s of streams) s.stop();
      });
    } catch {
      /* Media Session not fully supported */
    }

    return () => {
      try {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('stop', null);
      } catch {
        /* noop */
      }
    };
  }, [streams]);
}

/**
 * Shell-level dual-stream engine. Mount once under MobileWorkspace so
 * leaving the Audio tab does not tear down WebRTC / AudioContext.
 */
export function MobileAudioProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const { hasPermission } = usePermissions();
  const canSteamroom = hasPermission('steamroom');
  const canZeroday = hasPermission('steamroom') || hasPermission('zeroday');

  const getSteamroomUrl = useCallback(async () => {
    const { url } = await srsService.getPlayToken('steamroom');
    return url;
  }, []);

  const getZerodayUrl = useCallback(async () => {
    const { url } = await srsService.getPlayToken('zeroday');
    return url;
  }, []);

  // Hooks must be unconditional — gate usage via enabled flags on the result.
  const steamroomStream = useSquawkStream({
    label: 'FZN PRO',
    streamUrl: STEAMROOM_STREAM_URL,
    getStreamUrl: getSteamroomUrl,
  });

  const zerodayStream = useSquawkStream({
    label: 'FZN ZERODAY',
    streamUrl: ZERODAY_STREAM_URL,
    getStreamUrl: getZerodayUrl,
  });

  const steamroom = canSteamroom ? steamroomStream : null;
  const zeroday = canZeroday ? zerodayStream : null;

  const anyLive = Boolean(
    (steamroom?.status === 'live' || steamroom?.status === 'connecting') ||
      (zeroday?.status === 'live' || zeroday?.status === 'connecting'),
  );
  const anyIntended = Boolean(
    steamroom?.intendedLive || zeroday?.intendedLive,
  );

  const stopAll = useCallback(() => {
    steamroomStream.stop();
    zerodayStream.stop();
  }, [steamroomStream, zerodayStream]);

  // Depend on status strings so Media Session is not re-bound every render.
  const srStatus = steamroom?.status ?? null;
  const zdStatus = zeroday?.status ?? null;

  const sessionStreams = useMemo(() => {
    const list: Array<{
      label: string;
      status: SquawkStatus;
      stop: () => void;
      start: () => Promise<void>;
    }> = [];
    if (canSteamroom) {
      list.push({
        label: 'FZN PRO',
        status: srStatus ?? 'disconnected',
        stop: steamroomStream.stop,
        start: steamroomStream.start,
      });
    }
    if (canZeroday) {
      list.push({
        label: 'FZN ZERODAY',
        status: zdStatus ?? 'disconnected',
        stop: zerodayStream.stop,
        start: zerodayStream.start,
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- start/stop are stable enough; status drives rebind
  }, [canSteamroom, canZeroday, srStatus, zdStatus]);

  useMediaSession(sessionStreams);

  const value = useMemo(
    (): MobileAudioContextValue => ({
      steamroom,
      zeroday,
      canSteamroom,
      canZeroday,
      anyLive,
      anyIntended,
      stopAll,
    }),
    [
      steamroom,
      zeroday,
      canSteamroom,
      canZeroday,
      anyLive,
      anyIntended,
      stopAll,
    ],
  );

  return (
    <MobileAudioContext.Provider value={value}>
      {children}
    </MobileAudioContext.Provider>
  );
}

export function useMobileAudio(): MobileAudioContextValue {
  const ctx = useContext(MobileAudioContext);
  if (!ctx) {
    throw new Error('useMobileAudio must be used within MobileAudioProvider');
  }
  return ctx;
}
